<script setup>
import { onMounted, ref, watch, onBeforeUnmount, nextTick } from 'vue';
import { getChartThemeColors } from '../helpers/reportChartTheme';

const props = defineProps({
  data: { type: Array, default: () => [] },
  title: { type: String, default: 'Taxa de Perda por Etapa' },
});

const chartRef = ref(null);
let chartInstance = null;

const destroyChart = () => {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
};

const renderChart = async () => {
  await nextTick();
  destroyChart();
  if (!chartRef.value || !props.data.length) return;
  const ctx = chartRef.value.getContext && chartRef.value.getContext('2d');
  if (!ctx) return;
  const Chart = (await import('chart.js/auto')).default;
  const theme = getChartThemeColors();

  const sortedData = [...props.data].sort((a, b) => b.lossRate - a.lossRate);
  const labels = sortedData.map(d => d.stage);
  const lossRates = sortedData.map(d => d.lossRate);
  const lostItems = sortedData.map(d => d.lostItems);
  const totalItems = sortedData.map(d => d.totalItems);

  const colors = lossRates.map(rate => {
    if (rate >= 50) return 'rgba(239, 68, 68, 0.8)';
    if (rate >= 25) return 'rgba(245, 158, 11, 0.8)';
    return 'rgba(34, 197, 94, 0.8)';
  });

  chartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [
        {
          label: 'Taxa de Perda (%)',
          data: lossRates,
          backgroundColor: colors,
          borderRadius: 6,
          barPercentage: 0.6,
        },
      ],
    },
    options: {
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: context => {
              const idx = context.dataIndex;
              return `Perda: ${lossRates[idx]}% (${lostItems[idx]} de ${totalItems[idx]} items)`;
            },
          },
        },
      },
      scales: {
        x: {
          max: 100,
          ticks: { color: theme.tick, callback: v => v + '%' },
          grid: { color: theme.grid },
        },
        y: {
          ticks: { color: theme.tick },
          grid: { display: false },
        },
      },
      responsive: true,
      maintainAspectRatio: false,
    },
  });
};

onMounted(renderChart);
watch(() => props.data, renderChart, { deep: true });
onBeforeUnmount(destroyChart);
</script>

<template>
  <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-3">
    <h3 class="text-sm font-extrabold text-n-slate-12 mb-2">
      {{ title }}
    </h3>
    <div v-if="!data.length" class="text-sm text-n-slate-10 py-3 text-center">
      Nenhum dado de perda no período
    </div>
    <div v-else class="relative" :style="{ height: Math.max(200, data.length * 40) + 'px' }">
      <canvas ref="chartRef" />
    </div>
  </div>
</template>
